import { and, gt, not, or, reads } from '@ember/object/computed';
import { buildValidations, validator } from 'ember-cp-validations';
import { capitalize, underscore } from '@ember/string';
import { computed, defineProperty } from '@ember/object';
import { service } from '@ember/service';
import Model, { attr, belongsTo, hasMany } from '@ember-data/model';
import classic from 'ember-classic-decorator';
import moment from 'moment-timezone';

const Validations = buildValidations({
  // these should only be validated either way
  addressCity: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressCity',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressCity',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  addressState: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressState',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressState',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  addressStreet: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressStreet',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressStreet',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  addressZip: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressZip',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredAddressStreet'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.addressZip',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  birthDate: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.birthDate',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredBirthDate'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.birthDate',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  eligibleInsuranceProviderName: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.eligibleInsuranceProviderName',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.askForInsuranceInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.eligibleInsuranceProviderName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emailAddress: [
    validator('format', {
      type: 'email',
      allowBlank: true,
      disabled: reads('model.isCouple'),
      messageKey: 'errors.documentRequestContactInfoConnection.emailAddress',
    }),
    validator('presence', {
      presence: true,
      disabled: or('model.isMinor', 'model.isCouple'),
      messageKey: 'errors.documentRequestContactInfoConnection.emailAddress',
    }),
    validator('ds-error'),
  ],
  emergencyContactEmailAddress: [
    validator('format', {
      type: 'email',
      allowBlank: true,
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactEmailAddress',
      messageKey: 'errors.format.email',
    }),
    validator('presence', {
      presence: true,
      disabled: computed('model.{isRequiredEmergencyContactEmailAddress,isCouple}', function () {
        if (this.get('model.isCouple')) {
          return false;
        }

        return !this.get('model.isRequiredEmergencyContactEmailAddress');
      }),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactEmailAddress',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emergencyContactFirstName: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredEmergencyContactNameInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactFirstName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emergencyContactLastName: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredEmergencyContactNameInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactLastName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  emergencyContactPhoneNumber: [
    validator('ds-error'),
    validator('presence', {
      presence: true,
      disabled: computed(
        'model.{isRequiredEmergencyContactPhoneNumber,isRequiredPhoneNumber,isCouple}',
        function () {
          if (this.get('model.isCouple')) {
            return !(
              this.get('model.isRequiredEmergencyContactPhoneNumber') ||
              this.get('model.isRequiredPhoneNumber')
            );
          }

          return !this.get('model.isRequiredEmergencyContactPhoneNumber');
        }
      ),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactPhoneNumber',
      messageKey: 'errors.presence',
    }),
  ],
  emergencyContactType: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredEmergencyContactType'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.emergencyContactType',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  employmentStatus: [validator('ds-error')],
  firstName: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.firstName',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: false,
      descriptionKey: 'errors.documentRequestContactInfoConnection.firstName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  genderInfo: [validator('ds-error')],
  // these should only be validated if the relationship is not self
  insuranceGroupId: [validator('ds-error')],
  insuranceInsuredAddress: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredAddress',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredAddress',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredCity: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredCity',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredCity',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredDob: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredDob',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredDob',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredFname: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredFname',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredFname',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredLname: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredLname',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredLname',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredPhone: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredPhone',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredPhone',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredRelation: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredRelation',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredRelation',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredSex: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredSex',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredSex',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredState: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredState',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredState',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceInsuredZip: [
    validator('presence', {
      presence: true,
      disabled: computed('model.{validateInsuranceInfo,isDependent}', function () {
        return !this.get('model.validateInsuranceInfo') || !this.get('model.isDependent');
      }),
      dependentKeys: [
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
        'model.askForInsurance',
        'model.isDependent',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredZip',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.isPrimaryInsuredInfoRequired'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceInsuredZip',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceMemberId: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceMemberId',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.askForInsuranceInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceMemberId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insurancePlanId: [validator('ds-error')],
  insuranceEffectiveStartDate: [validator('ds-error')],
  insuranceEffectiveEndDate: [validator('ds-error')],
  isMinor: [validator('ds-error')],
  lastName: [
    validator('presence', {
      presence: true,
      disabled: false,
      descriptionKey: 'errors.documentRequestContactInfoConnection.lastName',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  middleName: [validator('ds-error')],
  nickname: [validator('ds-error')],
  phoneNumber: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateNonCoupleInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.phoneNumber',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: computed('model.{isRequiredPhoneNumber,isCouple,isMinor}', function () {
        if (this.get('model.isMinor') || this.get('model.isCouple')) return true;

        return !this.get('model.isRequiredPhoneNumber');
      }),
      descriptionKey: 'errors.documentRequestContactInfoConnection.phoneNumber',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  relationshipStatus: [validator('ds-error')],
  sex: [
    validator('presence', {
      presence: true,
      disabled: not('model.validateInsuranceInfo'),
      dependentKeys: [
        'model.validateInsuranceInfo',
        'currentPractice.claimAssistantSettingActive',
        'currentPractice.featureThirdPartyBilling',
      ],
      descriptionKey: 'errors.documentRequestContactInfoConnection.sex',
      messageKey: 'errors.presence',
    }),
    validator('presence', {
      presence: true,
      disabled: not('model.askForInsuranceInfo'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.sex',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  timeZone: [validator('ds-error')],
  insuranceCardFrontId: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredInsuranceInfoCard'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceCardFrontId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
  insuranceCardBackId: [
    validator('presence', {
      presence: true,
      disabled: not('model.isRequiredInsuranceInfoCard'),
      descriptionKey: 'errors.documentRequestContactInfoConnection.insuranceCardBackId',
      messageKey: 'errors.presence',
    }),
    validator('ds-error'),
  ],
});

@classic
export default class DocumentRequestContactInfoConnectionModel extends Model.extend(Validations) {
  @service store;
  @service session;
  @service currentPractice;
  @service intl;
  @service clientInsuranceAccess;

  @reads('session.currentClient.isCouple') isCouple;
  @and('insuranceFeatureEnabled', 'askForInsuranceInfo') validateInsuranceInfo;
  @not('isCouple') isNotCouple;
  @and('validateInsuranceInfo', 'isNotCouple') validateNonCoupleInsuranceInfo;
  @gt('insuranceInsuredRelation', 0) isDependent;

  @belongsTo('insurance-provider-name', { async: false }) eligibleInsuranceProviderName;
  @hasMany('insurance-card', { async: false }) insuranceCards;
  @attr('boolean') askForNickname;
  @attr('boolean') askForGenderIdentity;
  @attr('json', {
    defaultValue() {
      return [];
    },
  })
  demographicFormRequiredFields;
  @attr('boolean') askForInsurance;
  @attr() timeZone;
  @attr('boolean') practiceEnableEmailReminders;
  @attr('boolean') practiceEnableSmsReminders;
  @attr('boolean') practiceEnableVoiceReminders;
  @attr() firstName;
  @attr() lastName;
  @attr() middleName;
  @attr() nickname;
  @attr('boolean') isMinor;
  @attr() emailAddress;
  @attr() emailType;
  @attr('boolean') emailEmailConsentGiven;
  @attr('boolean') enableEmailReminders;
  @attr() phoneNumber;
  @attr() phoneType;
  @attr('boolean') phoneLeaveVoiceMessage;
  @attr('boolean') phoneLeaveTextMessage;
  @attr() enableSmsvoiceReminders;
  @attr() addressStreet;
  @attr() addressCity;
  @attr() addressState;
  @attr() addressZip;
  @attr('moment') birthDate;
  @attr() sex;
  @attr() genderInfo;
  @attr() relationshipStatus;
  @attr() employmentStatus;
  @attr() emergencyContactFirstName;
  @attr() emergencyContactLastName;
  @attr() emergencyContactType;
  @attr() emergencyContactPhoneNumber;
  @attr() emergencyContactPhoneType;
  @attr('boolean') emergencyContactPhoneLeaveVoiceMessage;
  @attr('boolean') emergencyContactPhoneLeaveTextMessage;
  @attr() emergencyContactEnableSmsvoiceReminders;
  @attr() emergencyContactEmailAddress;
  @attr() emergencyContactEmailType;
  @attr('boolean') emergencyContactEmailEmailConsentGiven;
  @attr('boolean') emergencyContactEnableEmailReminders;
  @attr() insuranceMemberId;
  @attr() insuranceGroupId;
  @attr() insurancePlanId;
  @attr() insuranceInsuredRelation;
  @attr() insuranceInsuredFname;
  @attr() insuranceInsuredLname;
  @attr('moment') insuranceInsuredDob;
  @attr() insuranceInsuredSex;
  @attr() insuranceInsuredPhone;
  @attr() insuranceInsuredAddress;
  @attr() insuranceInsuredCity;
  @attr() insuranceInsuredState;
  @attr() insuranceInsuredZip;
  @attr() insuranceCardFrontId;
  @attr() insuranceCardBackId;
  @attr('moment') insuranceEffectiveStartDate;
  @attr('moment') insuranceEffectiveEndDate;

  @and('eligibleInsuranceProviderName', 'insuranceMemberId') hasRequiredInsuranceInfo;

  static demographicFields = [
    'phoneNumber',
    'addressStreet',
    'birthDate',
    'emergencyContactName',
    'emergencyContactEmailAddress',
    'emergencyContactPhoneNumber',
    'insuranceInformation',
    'insuranceCardUploaded',
  ];

  init() {
    super.init(...arguments);

    this.constructor.demographicFields.forEach(fieldName => {
      defineProperty(
        this,
        `isRequired${capitalize(fieldName)}`,
        computed('demographicFormRequiredFields.[]', function () {
          return this.demographicFormRequiredFields.includes(underscore(fieldName));
        })
      );
    });
  }

  @computed('isCouple', 'isRequiredEmergencyContactName')
  get isRequiredEmergencyContactNameInfo() {
    if (this.isCouple) return false;

    return this.isRequiredEmergencyContactName;
  }

  @computed(
    'isRequiredEmergencyContactName',
    'isRequiredEmergencyContactEmailAddress',
    'isRequiredEmergencyContactPhoneNumber'
  )
  get isRequiredEmergencyContactType() {
    return (
      this.isRequiredEmergencyContactName ||
      this.isRequiredEmergencyContactEmailAddress ||
      this.isRequiredEmergencyContactPhoneNumber
    );
  }

  @computed('askForInsuranceInfo', 'isDependent')
  get isPrimaryInsuredInfoRequired() {
    return this.askForInsuranceInfo && this.isDependent;
  }

  @computed('askForInsurance', 'isRequiredInsuranceInformation')
  get askForInsuranceInfo() {
    return this.askForInsurance && this.isRequiredInsuranceInformation;
  }

  @computed('askForInsurance', 'isRequiredInsuranceCardUploaded')
  get isRequiredInsuranceInfoCard() {
    return this.askForInsurance && this.isRequiredInsuranceCardUploaded;
  }

  get autosaveAttrs() {
    let attrs = [];
    this.constructor.fields.forEach((_, name) => attrs.push(name));
    attrs.removeObject('askForNickname');
    attrs.removeObject('askForGenderIdentity');
    attrs.removeObject('askForInsurance');
    attrs.removeObject('practiceEnableEmailReminders');
    attrs.removeObject('practiceEnableSmsReminders');
    attrs.removeObject('practiceEnableVoiceReminders');
    attrs.removeObject('insuranceCards');
    attrs.removeObject('demographicFormRequiredFields');
    return attrs;
  }

  get insuranceFeatureEnabled() {
    return this.clientInsuranceAccess.claimAssistantOrManagedBilling;
  }

  autosaveSerialize() {
    let data = {};
    this.autosaveAttrs.forEach(name => {
      if (name === 'eligibleInsuranceProviderName' && this.get(name)) {
        return (data[name] = this.get(name).serialize({ includeId: true }));
      }

      data[name] = this.get(name);
    });
    return data;
  }

  autosaveRestore(data) {
    this.autosaveAttrs.forEach(name => {
      // eslint-disable-next-line no-prototype-builtins
      if (!data.hasOwnProperty(name)) {
        return;
      }

      if (
        name === 'eligibleInsuranceProviderName' &&
        data[name] &&
        typeof data[name] === 'object'
      ) {
        this.store.pushPayload(data[name]);
        return this.set(name, this.store.peekRecord('insurance-provider-name', data[name].data.id));
      }

      if (
        [
          'birthDate',
          'insuranceInsuredDob',
          'insuranceEffectiveStartDate',
          'insuranceEffectiveEndDate',
        ].includes(name)
      ) {
        let value = moment.parseZone(data[name]);
        if (!value.isValid()) return;

        return this.set(name, value);
      }

      this.set(name, data[name]);
    });
  }
}
